import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { Button, Heading, notify, Text } from '@/components/ui';

// Wizard's circular step indicator. Shows "current/steps" text inside a
// 48x48 circle whose stroke proportion matches progress through the flow.
// Single-purpose: lives here because the only consumer is the Wizard
// header — there is no general "circular progress" use case in the app.
const ProgressSteps = ({ current, steps }) => {
    const percentage = steps > 0 ? (current / steps) * 100 : 0;

    return (
        <div className="tw-relative tw-w-12 tw-h-12">
            <svg className="tw-w-full tw-h-full" viewBox="0 0 36 36">
                <g transform="rotate(-90 18 18)">
                    <circle cx="18" cy="18" r="16" fill="none" className="tw-stroke-border" strokeWidth="4" />
                    <circle
                        cx="18"
                        cy="18"
                        r="16"
                        fill="none"
                        className="tw-stroke-secondary"
                        strokeWidth="4"
                        strokeDasharray="100"
                        strokeDashoffset={100 - percentage}
                    />
                </g>
            </svg>
            <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
                <Text variant="control">{current}/{steps}</Text>
            </div>
        </div>
    );
};

// Wizard — multi-step flow over a single React Hook Form instance owned
// by the caller.
//
// API:
//
//   form        RHF instance (useForm result). Wizard reads form values
//               via form.watch() to evaluate per-step validity and
//               passes form down to every step component. Single source
//               of truth — no parallel `data` state, no onChangeField
//               factory, no onCompleted IoC. (required)
//
//   onSubmit    async (data) => void. Called with form.getValues() once
//               the user reaches the final step and the schema passes.
//               If onSubmit throws, the error message goes through
//               notify.error and the wizard stays put. (required)
//
//   onCancel    () => void. Called on Cancel from the first step.
//               Wizard is routing-agnostic — the caller decides what
//               cancel means. (required)
//
//   onClose     () => void. Called when the wizard finishes — either
//               immediately after a successful submission with no
//               successStep, or when the user clicks Close on the
//               success screen. (required)
//
//   steps       Array<{ Component, title, description, isValid? }>.
//               Each step provides:
//                 - Component: React component that receives { form }
//                   and reads/writes form fields directly (RHF).
//                 - title / description: header copy for this step.
//                 - isValid: optional (data) => boolean. Returns
//                   whether the user may advance from this step.
//                   Pure function of the form values — no form access
//                   inside, no async, no errors map. When absent the
//                   step is treated as auto-valid (display-only).
//
//   successStep Optional { Component, title, description }. Same
//               anatomy as a regular step. Rendered after a successful
//               submit; the footer collapses to a single right-aligned
//               Close button that triggers onClose.
//
//   stepTransitionDuration  Optional milliseconds for the slide+fade
//                           between steps. Default 200.

export function Wizard ({
    form,
    onCancel,
    onClose,
    onSubmit,
    steps,
    stepTransitionDuration = 200,
    successStep = null
}) {
    const data = form.watch();
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [step, setStep] = useState(0);

    const StepComponent = steps[step]?.Component;
    const SuccessComponent = successStep?.Component;
    const totalSteps = steps.length;

    const description = showSuccess ? successStep?.description : steps[step]?.description;
    const title = showSuccess ? successStep?.title : steps[step]?.title;

    const stepIsValid = steps[step]?.isValid?.(data) ?? true;

    const handleBack = () => {
        setStep(prev => Math.max(prev - 1, 0));
    };

    const handleNext = async () => {
        if (step === steps.length - 1) {
            await submit();
        } else {
            setStep(prev => prev + 1);
        }
    };

    const submit = async () => {
        setLoading(true);

        try {
            // Per-step `isValid` callbacks gate advancing through steps —
            // by the time we get here, all step-level conditions are met.
            // Schema-level errors (if any) propagate from the caller's
            // onSubmit (e.g. the API rejects bad data and onSubmit throws).
            await onSubmit(form.getValues());

            if (successStep) {
                setShowSuccess(true);
            } else {
                onClose();
            }
        } catch (err) {
            const message = err?.message ?? 'Something went wrong';
            notify.error(message);
        } finally {
            setTimeout(() => setLoading(false), 500);
        }
    };

    return (
        <div className="tw-relative tw-w-full tw-flex-1 tw-flex tw-flex-col tw-overflow-hidden">
            <AnimatePresence mode="wait">
                <motion.div
                    animate={{ x: 0, opacity: 1 }}
                    className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-w-full tw-p-6"
                    exit={{ x: '-100%', opacity: 0 }}
                    initial={{ x: '100%', opacity: 0 }}
                    key={step}
                    transition={{ duration: stepTransitionDuration / 1000 }}
                >
                    {/* Header */}
                    <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
                        <div className="tw-flex tw-flex-col">
                            <Heading as="h2" variant="title-lg">{title}</Heading>
                            <Text tone="muted" variant="body">{description}</Text>
                        </div>

                        {!showSuccess && (
                            <ProgressSteps current={step + 1} steps={totalSteps} />
                        )}
                    </div>

                    {showSuccess ? (
                        <>
                            <div className="tw-flex tw-flex-1 tw-basis-auto tw-min-h-0 tw-flex-col tw-overflow-y-auto tw-pr-2">
                                <SuccessComponent />
                            </div>

                            <div className="tw-mt-auto tw-flex tw-flex-none tw-justify-end tw-items-center tw-pt-6">
                                <Button
                                    appearance="solid"
                                    onClick={onClose}
                                    size="large"
                                    tone="primary"
                                    type="button"
                                >
                                    {i18next.t('ui.buttons.close')}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Step content area is itself a flex-col so a
                                step that wants to fill the vertical canvas
                                (e.g. one rendering a TransferList) can do
                                so via its own `tw-flex-1`. Content-sized
                                steps stack at the top exactly like before
                                — no visual change for them. */}
                            <div className="tw-flex tw-flex-1 tw-basis-auto tw-min-h-0 tw-flex-col tw-overflow-y-auto tw-pr-2">
                                {StepComponent && <StepComponent form={form} />}
                            </div>

                            {/* Mirrors the Edit page footer: outline
                                primary cancel/back on the left, solid
                                primary commit/progress on the right.
                                Both in the primary family — distinct
                                by intensity, paired by tone. Content-
                                sized, no fullWidth or min-width clamp,
                                so the visual matches Edit exactly. */}
                            <div className="tw-mt-auto tw-flex tw-flex-none tw-justify-between tw-items-center tw-pt-6">
                                <Button
                                    appearance="outline"
                                    disabled={loading}
                                    onClick={step === 0 ? onCancel : handleBack}
                                    size="large"
                                    tone="primary"
                                    type="button"
                                >
                                    {step === 0 ? i18next.t('ui.buttons.cancel') : i18next.t('ui.buttons.back')}
                                </Button>
                                <Button
                                    appearance="solid"
                                    disabled={!stepIsValid}
                                    loading={loading}
                                    onClick={handleNext}
                                    size="large"
                                    tone="primary"
                                    type="button"
                                >
                                    {step + 1 === totalSteps ? i18next.t('ui.buttons.submit') : i18next.t('ui.buttons.next')}
                                </Button>
                            </div>
                        </>
                    )}
                    {loading && (
                        <div className="tw-absolute tw-inset-0 tw-bg-background/70 tw-z-modal" />
                    )}
                </motion.div>
            </AnimatePresence>
        </div>
    );
}
